import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "icon", "iconPosition", "iconSize"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { TEXT_BUTTON_FONT_TYPES, TEXT_BUTTON_TYPES } from '../../constants';
import { TextButtonIcon } from '../TextButtonIcon/TextButtonIcon';
import { StyledTextButton } from './TextButton.styles';

/**
 * Renders a basic
 * [HTML button element](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button).
 *
 * While the styling of a text button resembles that of an `Anchor`, text buttons are
 * generally used for user actions and not for navigation. If you need to use a text button for
 * navigation and a regular `Anchor` does not suffice, make sure to set `as="a"` to help
 * assistive technologies better represent the purpose of the component.
 *
 * @since 5.0.0
 */
export var TextButton = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    icon = _ref.icon,
    iconPosition = _ref.iconPosition,
    iconSize = _ref.iconSize,
    rest = _objectWithoutProperties(_ref, _excluded);
  var startIcon;
  if (iconPosition === 'start' && icon) {
    startIcon = /*#__PURE__*/React.createElement(TextButtonIcon, {
      iconPosition: iconPosition,
      iconSize: iconSize
    }, icon);
  }
  var endIcon;
  if (iconPosition === 'end' && icon) {
    endIcon = /*#__PURE__*/React.createElement(TextButtonIcon, {
      iconPosition: iconPosition,
      iconSize: iconSize
    }, icon);
  }
  return /*#__PURE__*/React.createElement(StyledTextButton, _extends({
    ref: forwardedRef
  }, rest), startIcon, children, endIcon);
});
TextButton.displayName = 'TextButton';
TextButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The styled-components
   * [polymorphic "as"](https://styled-components.com/docs/api#as-polymorphic-prop) prop.
   *
   * By default, this is a basic `<button>` element, but if you are using this component for
   * navigation, you should set this to `as="a"`.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any,
  /** The type of the button. */
  buttonType: PropTypes.oneOf(Object.values(TEXT_BUTTON_TYPES)),
  /**
   * Text button content.
   */
  children: PropTypes.node,
  /** Set the button as disabled. */
  disabled: PropTypes.bool,
  /**
   * Determines the font used for the label. Limited to `body`, `bodyHeading`, `bodySmall` and
   * `bodySmallHeading`.
   */
  fontType: PropTypes.oneOf(Object.values(TEXT_BUTTON_FONT_TYPES)),
  /**
   * Add an `Icon` to the button.
   */
  icon: PropTypes.node,
  /**
   * Change the position of the `icon`.
   */
  iconPosition: PropTypes.oneOf(['start', 'end']),
  /**
   * This is not an exposed API property. It is only used internally.
   *
   * @ignore
   */
  iconSize: PropTypes.string,
  /** For use on dark or colored backgrounds. */
  inverse: PropTypes.bool
} : {};
TextButton.defaultProps = {
  as: 'button',
  buttonType: TEXT_BUTTON_TYPES.primary,
  disabled: undefined,
  fontType: 'bodyHeading',
  inverse: false,
  iconPosition: 'start',
  iconSize: 'sm'
};